import { useEffect, useState, useCallback } from 'react';
import { api } from '@/lib/api';

/**
 * useReferenceConfig — fetches the active company's Reference/Page-Number config.
 *
 * The running-number system is a Super-Admin, per-company opt-in. This hook
 * tells any page whether the feature is enabled for the given company, what
 * the NEXT auto-assigned number would be (read-only preview, does not consume
 * a number), and the company's chosen display TERM (e.g. "Page No.",
 * "Reference No.", "Job No.", "Order No.").
 *
 * Returns: { enabled, nextPreview, label, labelShort, loading, refresh }
 *   label       full term, e.g. "Page No."   (default "Page No.")
 *   labelShort  first word for badges/chips, e.g. "Page"
 */
const DEFAULT_LABEL = 'Page No.';

export function useReferenceConfig(companyId) {
  const [config, setConfig] = useState({ enabled: false, nextPreview: null, label: DEFAULT_LABEL });
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!companyId) {
      setConfig({ enabled: false, nextPreview: null, label: DEFAULT_LABEL });
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get('/orders/reference-config', {
        params: { company_id: companyId },
      });
      setConfig({
        enabled: !!data?.enabled,
        nextPreview: data?.next_reference_preview ?? null,
        label: (data?.label || DEFAULT_LABEL).trim() || DEFAULT_LABEL,
      });
    } catch {
      setConfig({ enabled: false, nextPreview: null, label: DEFAULT_LABEL });
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { refresh(); }, [refresh]);

  const label = config.label || DEFAULT_LABEL;
  // Short single-word form for tiny badges / chips ("Page", "Reference",
  // "Job", "Order"). Falls back to the full label if it has no spaces.
  const labelShort = (label.split(' ')[0] || label).replace(/\.$/, '');

  return {
    enabled: config.enabled,
    nextPreview: config.nextPreview,
    label,
    labelShort,
    loading,
    refresh,
  };
}
